/**
 * CLE-2811 — "Live from the AI": the AI's own words as it works (the worker streams the last few
 * thousand characters of its narration) and the last few things it did, in plain words. This is the
 * part of the wait worth reading — it is what the model is actually thinking about, not a status line.
 *
 * The box follows the newest line unless the reader has scrolled up to read something, and then offers a
 * way back down rather than yanking her there on the next poll.
 */

import React, { useLayoutEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";

import {
  LIVE_ACTIONS,
  LIVE_BEFORE_THINKING,
  LIVE_FROM_AI,
  LIVE_JUMP_TO_NEWEST,
  LIVE_NO_NOTES_YET,
  LIVE_WARMING_UP,
  liveStepLabel,
} from "../copy";
import { ActivityKind, TimelinePhase, activityKind } from "./progressModel";
import { SectionTitle, blink, fadeIn } from "./styles";

/** After this long in the modeling phase with no words, stop promising them within a minute. */
export const WARM_UP_PROMISE_MS = 75_000;

/** Within this many pixels of the bottom counts as "reading the newest line". */
const STICK_THRESHOLD_PX = 24;

const Panel = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100%;
  padding: 0.9rem 1rem;
  border: 1px solid var(--color-purple-2, #cecafe);
  border-radius: 8px;
  background: var(--purple-50, #fbf7ff);
`;

const Chip = styled.span`
  padding: 0.05rem 0.5rem;
  border-radius: 999px;
  border: 1px solid var(--surface-border);
  background: var(--surface-card, #ffffff);
  color: var(--text-color-secondary);
  font-size: 0.72rem;
  font-weight: 400;
`;

const Narration = styled.div<{ $hasMore: boolean }>`
  position: relative;
  font-size: 0.875rem;
  line-height: 1.5;

  /* About 14 lines, then it scrolls inside the box. */
  max-height: calc(14 * 1.5em);
  overflow-y: auto;
  overflow-x: hidden;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  color: var(--text-color);
  padding-right: 0.25rem;

  ${({ $hasMore }) =>
    $hasMore &&
    css`
      mask-image: linear-gradient(to bottom, transparent, #000 1.5em);
    `}
`;

const Cursor = styled.span`
  display: inline-block;
  width: 0.5em;
  height: 1.05em;
  margin-left: 0.1em;
  vertical-align: text-bottom;
  background: var(--primary-color);
  border-radius: 1px;
  animation: ${blink} 1.1s steps(1) infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.6;
  }
`;

const Muted = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-color-secondary);
`;

const JumpButton = styled.button`
  align-self: flex-end;
  margin-top: 0.35rem;
  padding: 0.1rem 0.6rem;
  border: 1px solid var(--surface-border);
  border-radius: 999px;
  background: var(--surface-card, #ffffff);
  color: var(--primary-color);
  font: inherit;
  font-size: 0.75rem;
  cursor: pointer;
`;

const Actions = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const Action = styled.li<{ $newest: boolean }>`
  display: flex;
  align-items: baseline;
  gap: 0.45rem;
  min-width: 0;
  font-size: 0.78rem;
  color: var(--text-color-secondary);
  opacity: ${({ $newest }) => ($newest ? 1 : 0.8)};

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ${({ $newest }) =>
    $newest &&
    css`
      animation: ${fadeIn} 0.35s ease-out;
    `}
`;

const ACTIVITY_ICON: Record<ActivityKind, string> = {
  read: "pi pi-book",
  search: "pi pi-search",
  write: "pi pi-pencil",
  other: "pi pi-angle-right",
};

interface ILiveFromAiProps {
  active: TimelinePhase;
  narration: string;
  activity: string[];
  model: string | null;
  turns: number | null;
  /** How long the AI has been modeling, for the warm-up line; null when unknown. */
  thinkingElapsedMs: number | null;
}

function LiveFromAi({
  active,
  narration,
  activity,
  model,
  turns,
  thinkingElapsedMs,
}: ILiveFromAiProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const stickRef = useRef(true);
  const [detached, setDetached] = useState(false);
  const modeling = active === "thinking" || active === "saving";
  const thinking = active === "thinking";

  // Follow the newest line — unless she scrolled up to read.
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (box && stickRef.current) box.scrollTop = box.scrollHeight;
  }, [narration]);

  const onScroll = () => {
    const box = boxRef.current;
    if (!box) return;
    const atBottom =
      box.scrollHeight - box.scrollTop - box.clientHeight < STICK_THRESHOLD_PX;
    stickRef.current = atBottom;
    if (atBottom === detached) setDetached(!atBottom);
  };

  const jumpToNewest = () => {
    const box = boxRef.current;
    if (!box) return;
    stickRef.current = true;
    setDetached(false);
    box.scrollTop = box.scrollHeight;
  };

  let body: React.ReactNode;
  if (!modeling) body = <Muted>{LIVE_BEFORE_THINKING}</Muted>;
  else if (narration.trim()) {
    body = (
      <Narration
        ref={boxRef}
        onScroll={onScroll}
        $hasMore={detached || narration.length > 600}
        aria-live="off"
        data-testid="live-narration"
      >
        {narration.trimEnd()}
        {thinking ? <Cursor aria-hidden /> : null}
      </Narration>
    );
  } else {
    body = (
      <Muted>
        {thinkingElapsedMs != null && thinkingElapsedMs > WARM_UP_PROMISE_MS
          ? LIVE_NO_NOTES_YET
          : LIVE_WARMING_UP}
      </Muted>
    );
  }

  return (
    <Panel aria-label={LIVE_FROM_AI}>
      <SectionTitle>
        <i
          className="pi pi-comment"
          style={{ color: "var(--primary-color)", fontSize: "0.9rem" }}
        />
        {LIVE_FROM_AI}
        <span className="flex gap-1 ml-auto">
          {model ? (
            <Chip title="The model the worker gave the AI">{model}</Chip>
          ) : null}
          {turns ? <Chip>{liveStepLabel(turns)}</Chip> : null}
        </span>
      </SectionTitle>

      {body}
      {detached && modeling && narration.trim() ? (
        <JumpButton type="button" onClick={jumpToNewest}>
          {LIVE_JUMP_TO_NEWEST} ↓
        </JumpButton>
      ) : null}

      {modeling && activity.length ? (
        <div className="mt-3 pt-2 border-top-1 surface-border">
          <div className="text-xs font-semibold text-color-secondary mb-1">
            {LIVE_ACTIONS}
          </div>
          <Actions>
            {activity.map((a, i) => (
              <Action
                // eslint-disable-next-line react/no-array-index-key
                key={`${i}:${a}`}
                $newest={i === activity.length - 1}
              >
                <i
                  className={ACTIVITY_ICON[activityKind(a)]}
                  style={{ fontSize: "0.72rem" }}
                />
                <span title={a}>{a}</span>
              </Action>
            ))}
          </Actions>
        </div>
      ) : null}
    </Panel>
  );
}

export default LiveFromAi;
